/**
 * TherapyInbox — Daily batch review of draft therapy notes.
 *
 * End-of-day workflow:
 * 1. All draft notes listed as cards
 * 2. Swipe/tap to approve each one
 * 3. "Approve All" for days where everything looks correct
 * 4. Edit button on each card for corrections
 * 5. Unapproved notes highlighted with gentle reminder
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Chip, Container, Divider,
  IconButton, Paper, Stack, TextField, Typography, Alert,
  CircularProgress, Collapse, Fade, Badge,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  CheckCircleOutline as ApproveOutlineIcon,
  Edit as EditIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Inbox as InboxIcon,
  ArrowBack as BackIcon,
  DoneAll as ApproveAllIcon,
  Refresh as RefreshIcon,
  ErrorOutline as ErrorIcon,
  HourglassEmpty as ProcessingIcon,
  Replay as RetryIcon,
} from '@mui/icons-material';
import { EASI_DESIGN } from '../../theme/design-tokens';
import { therapy, type TherapyNote } from '../../api/endpoints';

const d = EASI_DESIGN;

export default function TherapyInbox() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<TherapyNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedNote, setExpandedNote] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [approving, setApproving] = useState<string | null>(null);
  const [batchApproving, setBatchApproving] = useState(false);

  const loadDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await therapy.getInbox(50);
      const data = (res?.data as any)?.data || [];
      setDrafts(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load inbox.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const handleApprove = useCallback(async (note: TherapyNote, approvedText?: string) => {
    const key = `${note.patientId}:${note.sessionTimestamp}`;
    setApproving(key);
    try {
      await therapy.approveNote(note.patientId, note.sessionTimestamp, approvedText);
      setDrafts(prev => prev.filter(n => `${n.patientId}:${n.sessionTimestamp}` !== key));
      setEditingNote(null);
    } catch (err: any) {
      setError(err.message || 'Failed to approve note.');
    }
    setApproving(null);
  }, []);

  const handleBatchApprove = useCallback(async () => {
    setBatchApproving(true);
    try {
      const notes = drafts.map(n => ({
        patientId: n.patientId,
        sessionTimestamp: n.sessionTimestamp,
      }));
      await therapy.batchApprove(notes);
      setDrafts([]);
    } catch (err: any) {
      setError(err.message || 'Batch approve failed.');
    }
    setBatchApproving(false);
  }, [drafts]);

  const formatTimestamp = (ts: string) => {
    try {
      const date = new Date(ts);
      return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch { return ts; }
  };

  const getNotePreview = (note: TherapyNote) => {
    return note.approvedNote || note.rawDraft || note.soapNote?.objective || 'No content yet — processing may still be in progress.';
  };

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/')} size="small">
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={800} color={d.color.emeraldDarkest}>
              Notes Inbox
            </Typography>
            <Badge
              badgeContent={drafts.length}
              color="warning"
              sx={{ '& .MuiBadge-badge': { fontWeight: 700 } }}
            >
              <InboxIcon sx={{ color: d.color.emerald }} />
            </Badge>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Review and approve your session notes
          </Typography>
        </Box>
        <IconButton onClick={loadDrafts} disabled={loading} size="small">
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {loading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress sx={{ color: d.color.emerald }} />
        </Box>
      )}

      {/* Empty state */}
      {!loading && drafts.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            border: `1px solid ${d.color.emeraldBorder}`,
            borderRadius: 3,
            bgcolor: d.color.emeraldBg,
          }}
        >
          <ApproveIcon sx={{ fontSize: 56, color: d.color.emerald, mb: 1 }} />
          <Typography variant="h6" fontWeight={700} color={d.color.emeraldDarkest}>
            All caught up!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No draft notes awaiting review.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            sx={{ mt: 2, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
          >
            Back to Caseload
          </Button>
        </Paper>
      )}

      {/* Approve All button */}
      {!loading && drafts.length > 1 && (
        <Button
          fullWidth
          variant="contained"
          startIcon={batchApproving ? <CircularProgress size={20} color="inherit" /> : <ApproveAllIcon />}
          onClick={handleBatchApprove}
          disabled={batchApproving}
          sx={{
            mb: 2,
            py: 1.5,
            fontWeight: 700,
            textTransform: 'none',
            bgcolor: d.color.emerald,
            borderRadius: 2,
            '&:hover': { bgcolor: d.color.emeraldDark },
          }}
        >
          Approve All ({drafts.length} notes)
        </Button>
      )}

      {/* Draft note cards */}
      <Stack spacing={2}>
        {drafts.map(note => {
          const key = `${note.patientId}:${note.sessionTimestamp}`;
          const isExpanded = expandedNote === key;
          const isEditing = editingNote === key;
          const isApproving = approving === key;

          return (
            <Card
              key={key}
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: note.approvalStatus === 'needs_review' ? '#F59E0B' : d.color.slate200,
                borderWidth: note.approvalStatus === 'needs_review' ? 2 : 1,
                transition: 'all 200ms ease-out',
                '&:hover': {
                  borderColor: d.color.emerald,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                },
              }}
            >
              <CardContent sx={{ pb: '12px !important' }}>
                {/* Header row */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body1" fontWeight={700}>
                      {formatTimestamp(note.sessionTimestamp)}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      <Chip
                        label={note.inputMode === 'ambient' ? 'Recorded' : note.inputMode === 'dictation' ? 'Dictated' : 'Manual'}
                        size="small"
                        sx={{
                          bgcolor: note.inputMode === 'ambient' ? d.color.emeraldPale : d.color.bluePale,
                          color: note.inputMode === 'ambient' ? d.color.emeraldDarkest : d.color.blueDeep,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                      {note.durationMinutes > 0 && (
                        <Chip label={`${note.durationMinutes} min`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                      {note.serviceType && (
                        <Chip label={note.serviceType.replace('_', ' ')} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                      {/* Processing status indicator */}
                      {(note as any).processingStatus === 'processing' && (
                        <Chip
                          icon={<CircularProgress size={12} sx={{ color: '#F59E0B' }} />}
                          label="Processing"
                          size="small"
                          sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      )}
                      {(note as any).processingStatus === 'failed' && (
                        <Chip
                          icon={<ErrorIcon sx={{ fontSize: '14px !important', color: '#DC2626' }} />}
                          label="Failed"
                          size="small"
                          sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      )}
                      {(note as any).processingStatus === 'completed' && (
                        <Chip
                          icon={<ApproveIcon sx={{ fontSize: '14px !important', color: '#059669' }} />}
                          label="Ready"
                          size="small"
                          sx={{ bgcolor: d.color.emeraldPale, color: d.color.emeraldDarkest, fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      )}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => { setEditingNote(isEditing ? null : key); setEditText(getNotePreview(note)); }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleApprove(note)}
                      disabled={isApproving}
                      sx={{
                        color: d.color.emerald,
                        '&:hover': { bgcolor: d.color.emeraldPale },
                      }}
                    >
                      {isApproving ? <CircularProgress size={20} /> : <ApproveIcon />}
                    </IconButton>
                  </Stack>
                </Stack>

                {/* Goals targeted */}
                {note.goalsTargeted && note.goalsTargeted.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    {note.goalsTargeted.map((g: any, i: number) => (
                      <Chip
                        key={i}
                        label={`${g.goalText?.slice(0, 30) || 'Goal'}${g.accuracyPct != null ? ` ${g.accuracyPct}%` : ''}`}
                        size="small"
                        sx={{ bgcolor: '#F3F4F6', fontSize: '0.7rem' }}
                      />
                    ))}
                  </Stack>
                )}

                {/* Preview / Expand */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: isExpanded ? 999 : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    cursor: 'pointer',
                  }}
                  onClick={() => setExpandedNote(isExpanded ? null : key)}
                >
                  {getNotePreview(note)}
                </Typography>

                {/* SOAP note details (expanded) */}
                <Collapse in={isExpanded}>
                  {note.soapNote && (note.soapNote.subjective || note.soapNote.objective) && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 1.5 }} />
                      {['subjective', 'objective', 'assessment', 'plan'].map(section => {
                        const content = (note.soapNote as any)?.[section];
                        if (!content) return null;
                        return (
                          <Box key={section} sx={{ mb: 1.5 }}>
                            <Typography variant="caption" fontWeight={700} color={d.color.emeraldDark} sx={{ textTransform: 'uppercase' }}>
                              {section}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {content}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Collapse>

                {/* Toggle expand */}
                <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                  <IconButton size="small" onClick={() => setExpandedNote(isExpanded ? null : key)}>
                    {isExpanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                  </IconButton>
                </Box>

                {/* Edit mode */}
                <Collapse in={isEditing}>
                  <Divider sx={{ my: 1.5 }} />
                  <TextField
                    multiline
                    minRows={3}
                    maxRows={10}
                    fullWidth
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      onClick={() => setEditingNote(null)}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleApprove(note, editText)}
                      disabled={isApproving}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        bgcolor: d.color.emerald,
                        '&:hover': { bgcolor: d.color.emeraldDark },
                      }}
                    >
                      Save & Approve
                    </Button>
                  </Stack>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Container>
  );
}
